import { DEFAULT_GRID_ROWS } from '../../PageConfigProvider/constants';
import type {
  Position,
  PositionedItem,
} from '../../PageConfigProvider/types/item';
import {
  INITIAL_GHOST_GRID_DIMENSIONS,
  MIN_ITEM_HEIGHT_CELLS,
  MIN_ITEM_WIDTH_CELLS,
} from '../constants';
import {
  buildOccupancyMatrix,
  isAreaFree,
  occupyArea,
} from './grid-layout-utils';

export type GhostPlaceholder = Pick<PositionedItem, 'id' | 'position'>;

type GridDimensions = {
  rows: number;
  columns: number;
};

type Axis = 'horizontal' | 'vertical';

const dimensionProps = {
  horizontal: {
    delta: 'x' as const,
    size: 'width' as const,
    minSize: MIN_ITEM_WIDTH_CELLS,
  },
  vertical: {
    delta: 'y' as const,
    size: 'height' as const,
    minSize: MIN_ITEM_HEIGHT_CELLS,
  },
} satisfies Record<
  Axis,
  {
    delta: keyof Position;
    size: keyof Position;
    minSize: number;
  }
>;

/**
 * Generate placeholder "ghost" elements that represent available space in the editable grid where new items can be created.
 *
 * - Greedy, heuristic-based algorithm that reuses existing items dimensions.
 * - Replicates items in all four directions to create a set of non-overlapping ghost placeholders.
 * - Designed to minimize empty space and create nested grid-like layouts in a few clicks.
 */
export function calculateGhosts({
  items,
  grid,
  isReport,
}: {
  items: PositionedItem[];
  grid: GridDimensions;
  isReport?: boolean;
}): GhostPlaceholder[] {
  if (items.length === 0) {
    const initial = createInitialGridGhosts(grid);
    if (isReport) {
      const matrix = buildOccupancyMatrix(grid, []);
      // Occupy initial ghosts so bottom strip avoids overlap
      for (const g of initial) {
        occupyArea(g.position, matrix);
      }
    }
    return initial;
  }

  const matrix = buildOccupancyMatrix(grid, items);

  const sortedItems = [...items].sort(
    (a, b) =>
      b.position.width * b.position.height -
      a.position.width * a.position.height
  );

  const allGhosts: GhostPlaceholder[] = [];
  let ghostId = 0;

  for (const { position: template } of sortedItems) {
    const seedItems: Position[] = [template];

    const horizontalClones = replicateInDirection(
      template,
      'horizontal',
      grid,
      matrix
    );

    for (const pos of horizontalClones) {
      allGhosts.push({ id: `ghost-${ghostId++}`, position: pos });
      seedItems.push(pos);
    }

    for (const seed of seedItems) {
      const verticalClones = replicateInDirection(
        seed,
        'vertical',
        grid,
        matrix
      );
      for (const pos of verticalClones) {
        allGhosts.push({ id: `ghost-${ghostId++}`, position: pos });
      }
    }
  }

  expandGhostsToFill(allGhosts, grid, matrix);
  return allGhosts;
}

function createInitialGridGhosts(grid: GridDimensions): GhostPlaceholder[] {
  const { rows: numRows, columns: numCols } = INITIAL_GHOST_GRID_DIMENSIONS;
  if (numRows === 0 || numCols === 0) return [];

  const ghostWidth = Math.floor(grid.columns / numCols);
  const ghostHeight = Math.floor(
    Math.min(grid.rows, DEFAULT_GRID_ROWS) / numRows
  );

  const ghosts: GhostPlaceholder[] = [];
  for (let r = 0; r < numRows; r++) {
    for (let c = 0; c < numCols; c++) {
      ghosts.push({
        id: `ghost-initial-${c}-${r}`,
        position: {
          x: c * ghostWidth,
          y: r * ghostHeight,
          width: ghostWidth,
          height: ghostHeight,
        },
      });
    }
  }
  return ghosts;
}

function replicateInDirection(
  base: Position,
  dir: Axis,
  grid: GridDimensions,
  matrix: boolean[][]
): Position[] {
  const results: Position[] = [];
  const { delta, size, minSize } = dimensionProps[dir];
  const limit = dir === 'horizontal' ? grid.columns : grid.rows;
  const directions = [1, -1];

  for (const direction of directions) {
    let nextPos = base[delta] + direction * base[size];
    let lastPlaced: Position | null = null;

    while (
      (direction > 0 && nextPos + base[size] <= limit) ||
      (direction < 0 && nextPos >= 0)
    ) {
      const pos: Position = { ...base, [delta]: nextPos };
      if (!isAreaFree(pos, grid, matrix)) {
        break;
      }
      occupyArea(pos, matrix);
      results.push(pos);
      lastPlaced = pos;
      nextPos += direction * base[size];
    }

    const spanStart =
      direction > 0 ? nextPos : (lastPlaced?.[delta] ?? base[delta]);
    const maxLength = computeFreeSpan(
      base,
      dir,
      direction,
      spanStart,
      grid,
      matrix
    );

    if (maxLength >= minSize) {
      const pos: Position = { ...base, [size]: maxLength };
      pos[delta] = direction > 0 ? spanStart : spanStart - maxLength;
      if (isAreaFree(pos, grid, matrix)) {
        occupyArea(pos, matrix);
        results.push(pos);
      }
    } else if (lastPlaced && maxLength > 0) {
      if (direction > 0) {
        lastPlaced[size] += maxLength;
      } else {
        lastPlaced[delta] -= maxLength;
        lastPlaced[size] += maxLength;
      }
    }
  }

  return results;
}

function computeFreeSpan(
  base: Position,
  dir: Axis,
  direction: number,
  start: number,
  grid: GridDimensions,
  matrix: boolean[][]
): number {
  const isHorizontal = dir === 'horizontal';
  const gridLimit = isHorizontal ? grid.columns : grid.rows;

  let length = 0;
  let major = direction > 0 ? start : start - 1;

  while (major >= 0 && major < gridLimit) {
    const slice: Position = {
      x: isHorizontal ? major : base.x,
      y: isHorizontal ? base.y : major,
      width: isHorizontal ? 1 : base.width,
      height: isHorizontal ? base.height : 1,
    };

    if (!isAreaFree(slice, grid, matrix)) return length;
    length++;
    major += direction;
  }

  return length;
}

function expandGhostsToFill(
  ghosts: GhostPlaceholder[],
  grid: GridDimensions,
  matrix: boolean[][]
): void {
  ghosts.sort(
    (a, b) =>
      a.position.x + a.position.width - (b.position.x + b.position.width)
  );

  for (const ghost of ghosts) {
    const p = ghost.position;

    (['horizontal', 'vertical'] as const).forEach((axis) => {
      const { delta, size } = dimensionProps[axis];

      const backwardSpan = computeFreeSpan(p, axis, -1, p[delta], grid, matrix);
      if (backwardSpan > 0) {
        p[delta] -= backwardSpan;
        p[size] += backwardSpan;
        occupyArea({ ...p, [size]: backwardSpan, [delta]: p[delta] }, matrix);
      }

      const forwardSpan = computeFreeSpan(
        p,
        axis,
        1,
        p[delta] + p[size],
        grid,
        matrix
      );
      if (forwardSpan > 0) {
        const oldSize = p[size];
        p[size] += forwardSpan;
        occupyArea(
          { ...p, [delta]: p[delta] + oldSize, [size]: forwardSpan },
          matrix
        );
      }
    });
  }
}
